import { useCallback } from 'react';
import { useDataTypes } from './useDataTypes';
import { useOperators } from './useOperators';
import { useFormatterTypes } from './useFormatterTypes';
import type { StrategyNode } from '../types/BaseNode';
import { resolveTemplate } from '../utils/templateParser';

/**
 * Hook to handle automatic naming of nodes based on their configuration and templates.
 */
export function useAutoNaming() {
    const { getDataTypeById, getOptionsFromCache, loading: dataLoading } = useDataTypes();
    const { getFormatterTypeById, loading: formatterLoading } = useFormatterTypes();
    const { getOperatorByValue } = useOperators();

    const loading = dataLoading || formatterLoading;

    /**
     * Resolve a single config value to a human-readable label.
     * - Uses inline options from the spec when available
     * - Uses the options cache for options_source fields
     * - Falls back to the raw value for plain fields (numbers, strings that aren't IDs)
     * - Returns null ONLY for inline-options fields where a match wasn't found (to avoid wrong label)
     */
    const resolveValue = useCallback((key: string, value: any, typeId?: string): string | null => {
        if (value === undefined || value === null) return '';

        if (typeId) {
            const dataType = getDataTypeById(typeId);
            const prop = dataType?.properties.find(p => p.key === key);

            if (prop) {
                // 1. Check inline options (always available once data types are loaded)
                if (prop.options && prop.options.length > 0) {
                    const opt = prop.options.find((p: any) => {
                        const optVal = (p && typeof p === 'object' && 'value' in p) ? p.value : p;
                        return String(optVal) === String(value);
                    });

                    if (opt) {
                        return (opt && typeof opt === 'object' && 'label' in opt) ? opt.label : String(opt);
                    }
                    // Inline options exist but value not found — strict: return null
                    return null;
                }

                // 2. Check options_source cache (may not be populated yet)
                const source = prop.options_source;
                if (source) {
                    const cached = getOptionsFromCache(source);
                    if (cached) {
                        const opt = cached.find((p: any) => String(p.value) === String(value));
                        if (opt) return opt.label;
                        // Option not found in cache — use raw value (e.g. "TTF", "NBP" are human-readable)
                        return String(value);
                    }
                    // Not in cache yet — return null to indicate we are waiting for labels
                    return null;
                }
            }
        }

        // Non-option fields: booleans or numbers or plain strings
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        return String(value);
    }, [getDataTypeById, getOptionsFromCache]);



    const getSuggestedName = useCallback((node: StrategyNode): string | null => {
        // Don't run until data types are loaded
        if (loading) return null;

        if (node.type === 'Single') {
            const config = (node as any).config || {};
            const typeId = config.typeId;
            if (!typeId) return null;

            const dataType = getDataTypeById(typeId);
            if (!dataType) return null;
            if (!dataType.naming_template) return null;

            return resolveTemplate(dataType.naming_template, config.props || {}, (k, v) => resolveValue(k, v, typeId));
        }

        if (node.type === 'Composite') {
            const left = node.config.left;
            const right = node.config.right;
            if (!left?.title || !right?.title) return null;

            const opValue = node.config.operator;
            const operator = getOperatorByValue(opValue);
            const opLabel = operator?.label || opValue || '/';

            return `${left.title} ${opLabel} ${right.title}`;
        }

        if (node.type === 'PnL') {
            const entry = node.config.entry;
            const exit = node.config.exit;
            if (!entry?.title || !exit?.title) return null;
            return `PnL(${entry.title} → ${exit.title})`;
        }

        if (node.type === 'Variance') {
            const target = node.config.target;
            if (!target?.title) return null;
            return `Variance(${target.title})`;
        }

        if (node.type === 'Formatter') {
            const typeId = node.config.typeId;
            const child = node.config.child;
            if (!child?.title) return null;

            const formatterType = getFormatterTypeById(typeId);
            if (!formatterType) return null;

            // Optional: Support naming templates for formatters too if they exist
            if ((formatterType as any).naming_template) {
                return resolveTemplate((formatterType as any).naming_template, node.config.props || {}, (k, v) => resolveValue(k, v, typeId));
            }

            return `${formatterType.label}(${child.title})`;
        }

        return null;
    }, [loading, getDataTypeById, getFormatterTypeById, getOperatorByValue, resolveValue]);

    return { getSuggestedName, loading };
}
